// src/components/gavion/domain/dialer/queueModel.ts
// TS mirror of SQL private.next_lead_id / claim_specific_lead eligibility (spec 04 §2). Same cases as pgTAP.
// Base eligibility and ordering come from the batch 1 queue key (leadQueue.ts); this module adds
// the session filter, calling hours, block-reason diagnostics and the manual-dial checks.
import {
  QUEUE_EXCLUDED_STATUSES,
  queueOrderKey,
  type QueueContext as BaseQueueContext,
} from "../leadQueue";
import { withinCallingHours, type CallingHours } from "../time";
import { torontoDate, torontoMidnight } from "../time/toronto"; // GAVION callbacks today
import type { QueueLead, SessionFilter } from "./types";

export type QueueContext = Omit<BaseQueueContext, "me"> & {
  me: number;
  filter: SessionFilter;
  hours: CallingHours;
  defaultTimezone: string;
  /**
   * GAVION test mode: the caller is an active admin (SQL private.test_mode_admin). With filter.test_mode the queue
   * serves test leads only, calling hours lifted (private.test_mode_on); a manual dial of a test lead skips the hours.
   */
  admin?: boolean;
};
export type BlockReason =
  | "invalid_phone"
  | "suppressed"
  | "status_blocked"
  | "cooling_down"
  | "max_attempts"
  | "assigned_other"
  | "locked"
  | "filtered"
  | "not_due"
  | "outside_calling_hours";
export type PickResult = {
  lead: QueueLead | null;
  reason: "ok" | "queue_empty" | "outside_calling_hours";
};

const lockedByOther = (l: QueueLead, ctx: QueueContext): boolean =>
  l.locked_until !== null &&
  Date.parse(l.locked_until) >= ctx.now.getTime() &&
  l.locked_by !== ctx.me;

const matchesFilter = (l: QueueLead, f: SessionFilter): boolean =>
  (!f.trades?.length || (l.trade !== null && f.trades.includes(l.trade))) &&
  (!f.regions?.length || (l.region !== null && f.regions.includes(l.region)));

const inHours = (l: QueueLead, ctx: QueueContext): boolean =>
  withinCallingHours(ctx.now, l.timezone || ctx.defaultTimezone, ctx.hours);

/** GAVION test mode: the session is an admin's « Mode test » (SQL private.test_mode_on). */
export const testModeOn = (ctx: Pick<QueueContext, "admin" | "filter">) =>
  !!ctx.admin && ctx.filter.test_mode === true;

/**
 * GAVION test mode: in « Mode test » a test lead's spacing gates are lifted (SQL queue_preview / next_lead_id, 58_):
 * cooldown, max attempts, a future callback and the 20 h retry gap. The key is computed on a relaxed copy; an
 * attempted test lead stays in bucket 5 (its last_called_at is dropped, so several attempted test leads keep only the
 * id order).
 */
const queueKey = (l: QueueLead, ctx: QueueContext) =>
  queueOrderKey(
    l.is_test && testModeOn(ctx)
      ? {
          ...l,
          cooldown_until: null,
          attempt_count: Math.min(l.attempt_count, ctx.maxAttempts - 1),
          last_called_at: null,
          next_callback_at:
            l.next_callback_at &&
            Date.parse(l.next_callback_at) <= ctx.now.getTime()
              ? l.next_callback_at
              : null,
        }
      : l,
    ctx,
  );

/** GAVION test mode: a manual dial of a test lead by an admin skips the calling hours (SQL claim_specific_lead). */
const manualInHours = (l: QueueLead, ctx: QueueContext): boolean =>
  (!!l.is_test && !!ctx.admin) || inHours(l, ctx);

/** Why the batch 1 queue key rejects a lead (diagnostics only; eligibility is the key). */
function baseBlockReason(l: QueueLead, ctx: QueueContext): BlockReason {
  const now = ctx.now.getTime();
  if (!l.phone_e164 || !l.phone_valid) return "invalid_phone";
  if (QUEUE_EXCLUDED_STATUSES.includes(l.prospect_status))
    return "status_blocked";
  if (ctx.suppressedPhones.has(l.phone_e164)) return "suppressed";
  if (l.cooldown_until && Date.parse(l.cooldown_until) > now)
    return "cooling_down";
  if (l.attempt_count >= ctx.maxAttempts && !l.next_callback_at)
    return "max_attempts";
  if (l.assigned_sales_id !== null && l.assigned_sales_id !== ctx.me)
    return "assigned_other";
  if (lockedByOther(l, ctx)) return "locked";
  return "not_due";
}

export function blockReason(
  l: QueueLead,
  ctx: QueueContext,
): BlockReason | null {
  if (queueKey(l, ctx) === null) return baseBlockReason(l, ctx);
  if (!matchesFilter(l, ctx.filter)) return "filtered";
  // GAVION test mode: test leads only in « Mode test », never otherwise; « Mode test » lifts the time gates
  const test = testModeOn(ctx);
  if (!!l.is_test !== test) return "filtered";
  if (!test && !inHours(l, ctx)) return "outside_calling_hours";
  return null;
}

const BUCKETS: Record<string, 1 | 2 | 3 | 4 | 5> = {
  "1": 1,
  "2": 2,
  "30": 3,
  "31": 4,
  "4": 5,
};

/** 1 my due callback, 2 other due callback, 3 my never-called, 4 unassigned never-called, 5 retry. Eligible leads only. */
export function bucketOf(l: QueueLead, ctx: QueueContext): 1 | 2 | 3 | 4 | 5 {
  const key = queueKey(l, ctx);
  if (key === null) throw new RangeError(`lead ${l.id} is not queue-eligible`);
  return key[0] === "3" ? BUCKETS[key.slice(0, 2)] : BUCKETS[key[0]];
}

/**
 * GAVION callbacks today (SQL 67_ queue_preview): within the due callbacks (buckets 1, 2), today's (from the start of
 * the Toronto day) first by time, then older overdue ones, most recent first. The SQL task rule (a due « Rappeler »
 * task) has no twin here: the demo's tasks never feed the queue.
 */
function compareCallbacks(a: QueueLead, b: QueueLead, ctx: QueueContext) {
  const dayStart = torontoMidnight(torontoDate(ctx.now)).getTime();
  const ta = Date.parse(a.next_callback_at as string);
  const tb = Date.parse(b.next_callback_at as string);
  const oa = ta < dayStart;
  const ob = tb < dayStart;
  if (oa !== ob) return oa ? 1 : -1;
  const d = oa ? tb - ta : ta - tb;
  return d !== 0 ? Math.sign(d) : a.id - b.id;
}

/** Claim order. Both leads must be queue-eligible. */
export function compareQueue(
  a: QueueLead,
  b: QueueLead,
  ctx: QueueContext,
): number {
  const ka = queueKey(a, ctx) ?? "";
  const kb = queueKey(b, ctx) ?? "";
  if (ka[0] === kb[0] && (ka[0] === "1" || ka[0] === "2"))
    return compareCallbacks(a, b, ctx);
  return ka < kb ? -1 : ka > kb ? 1 : 0;
}

export function previewQueue(
  leads: readonly QueueLead[],
  ctx: QueueContext,
  limit: number,
): QueueLead[] {
  return leads
    .filter((l) => blockReason(l, ctx) === null)
    .sort((a, b) => compareQueue(a, b, ctx))
    .slice(0, limit);
}

export function pickNext(
  leads: readonly QueueLead[],
  ctx: QueueContext,
): PickResult {
  const [lead] = previewQueue(leads, ctx, 1);
  if (lead) return { lead, reason: "ok" };
  if (testModeOn(ctx)) return { lead: null, reason: "queue_empty" }; // GAVION test mode
  const open = withinCallingHours(ctx.now, ctx.defaultTimezone, ctx.hours);
  return { lead: null, reason: open ? "queue_empty" : "outside_calling_hours" };
}

/** Manual dial (spec 04 §2.5): only phone validity, suppression, foreign lease and calling hours block. */
export function specificBlockReason(
  l: QueueLead,
  ctx: QueueContext,
): "suppressed" | "locked" | "outside_calling_hours" | "invalid_phone" | null {
  if (!l.phone_e164 || !l.phone_valid || l.prospect_status === "bad_number")
    return "invalid_phone";
  if (
    ctx.suppressedPhones.has(l.phone_e164) ||
    l.prospect_status === "do_not_call"
  )
    return "suppressed";
  if (lockedByOther(l, ctx)) return "locked";
  if (!manualInHours(l, ctx)) return "outside_calling_hours"; // GAVION test mode
  return null;
}

/**
 * GAVION contact-call: manual dial of a contact's number (SQL claim_contact_call). The lead's own line is not
 * checked (it is not the one dialled); the contact's number must not be suppressed and the lead not do_not_call.
 */
export function contactCallBlockReason(
  l: QueueLead,
  contactE164: string,
  ctx: QueueContext,
): "suppressed" | "locked" | "outside_calling_hours" | null {
  if (
    ctx.suppressedPhones.has(contactE164) ||
    l.prospect_status === "do_not_call"
  )
    return "suppressed";
  if (lockedByOther(l, ctx)) return "locked";
  if (!manualInHours(l, ctx)) return "outside_calling_hours"; // GAVION test mode
  return null;
}
